import React from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import useTechSession from '../../hooks/useTechSession';
import useLanguage from '../../hooks/useLanguage';
import TechnicianLayout from '../../components/tech/TechnicianLayout';
import TechLogin from './TechLogin';
import StartDay from './StartDay';
import TaskList from './TaskList';
import TaskDetail from './TaskDetail';
import PostFixInterview from './PostFixInterview';
import EndDay from './EndDay';
import SafetyDashboard from './SafetyDashboard';

/**
 * Root component for the technician mobile app.
 * Handles login gate and routing within TechnicianLayout.
 */
export default function TechApp() {
  const { isLoggedIn } = useTechSession();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();

  // Not logged in — show login
  if (!isLoggedIn) {
    return <TechLogin onLogin={() => navigate('/tech/start-day')} />;
  }

  // Logged in — show app with layout
  return (
    <TechnicianLayout isRTL={isRTL} t={t}>
      <Routes>
        <Route path="start-day" element={<StartDay />} />
        <Route path="tasks" element={<TaskList />} />
        <Route path="task/:taskId" element={<TaskDetail />} />
        <Route path="interview/:taskId" element={<PostFixInterview />} />
        <Route path="end-day" element={<EndDay />} />
        <Route path="safety" element={<SafetyDashboard />} />
        <Route path="profile" element={<TechProfile />} />
        <Route path="*" element={<Navigate to="/tech/start-day" replace />} />
      </Routes>
    </TechnicianLayout>
  );
}

/**
 * Simple profile page showing session info + logout.
 */
function TechProfile() {
  const { session, logout } = useTechSession();
  const { language, setLanguage, t, languages } = useLanguage();
  const navigate = useNavigate();

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>{t.profile}</h2>

      <div style={styles.card}>
        <div style={styles.avatar}>
          {session?.fullName?.[0]?.toUpperCase() || '?'}
        </div>
        <h3 style={{ margin: '8px 0 4px', fontSize: 18 }}>{session?.fullName}</h3>
        <div style={{ color: '#8b949e', fontSize: 13 }}>{session?.projectName}</div>
      </div>

      <div style={styles.section}>
        <div style={styles.row}>
          <span style={styles.label}>ID</span>
          <span style={styles.val}>{session?.technicianId?.slice(-8)}</span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>Project</span>
          <span style={styles.val}>{session?.projectName}</span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>Language</span>
          <span style={styles.val}>{languages[language]?.nativeName}</span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>Logged in</span>
          <span style={styles.val}>
            {session?.loginTime ? new Date(session.loginTime).toLocaleTimeString() : '-'}
          </span>
        </div>
      </div>

      {/* Language switch */}
      <div style={styles.section}>
        <div style={{ fontSize: 13, color: '#8b949e', marginBottom: 8 }}>{t.selectLanguage}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {Object.entries(languages).map(([code, info]) => (
            <button
              key={code}
              onClick={() => setLanguage(code)}
              style={{
                ...styles.langBtn,
                ...(language === code ? styles.langBtnActive : {}),
              }}
            >
              {info.flag} {info.nativeName}
            </button>
          ))}
        </div>
      </div>

      <button
        onClick={() => { logout(); navigate('/tech'); }}
        style={styles.logoutBtn}
      >
        {t.logout}
      </button>
    </div>
  );
}

const styles = {
  card: {
    background: '#161b22', borderRadius: 16, padding: '24px',
    textAlign: 'center', marginBottom: 14,
  },
  avatar: {
    width: 56, height: 56, borderRadius: '50%', background: '#6c5ce7',
    color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 24, fontWeight: 700,
  },
  section: {
    background: '#161b22', borderRadius: 12, padding: '14px 16px', marginBottom: 12,
  },
  row: {
    display: 'flex', justifyContent: 'space-between', padding: '6px 0',
    borderBottom: '1px solid #30363d20',
  },
  label: { fontSize: 13, color: '#8b949e' },
  val: { fontSize: 13, color: '#e6edf3', fontWeight: 600 },
  langBtn: {
    flex: 1, padding: '10px 8px', background: '#0d1117', border: '1px solid #30363d',
    borderRadius: 8, color: '#8b949e', cursor: 'pointer', fontSize: 12,
    fontFamily: 'inherit',
  },
  langBtnActive: {
    borderColor: '#6c5ce7', color: '#e6edf3', background: '#6c5ce720',
  },
  logoutBtn: {
    width: '100%', padding: '14px', background: '#30363d', border: 'none',
    borderRadius: 12, color: '#e6edf3', fontSize: 16, fontWeight: 600,
    cursor: 'pointer', marginTop: 16, fontFamily: 'inherit',
  },
};
